import { useCallback, useEffect } from "react";
import useMap from "@alpaca-travel/react-mapbox-gl/use-map";

import { useSubscribe, TargetStore } from "@/use-target-store";
import useSelection from "@/use-selection";
import { MIN_WIDTH_FOR_PADDING_LEFT_APPLY } from "@/types";
import useIndication from "@/use-indication";
import useMapDimensions from "./use-map-dimensions";
import { LngLat, LngLatBounds } from "mapbox-gl";

const LEFT_PADDING = 380;
const DEFAULT_PADDING = { top: 160, bottom: 100, left: 20, right: 20 };

const useTargetApply = () => {
  const { ampolService } = useSelection();
  const { ampolServices: indicatedAmpolServices } = useIndication();
  const { dimensions } = useMapDimensions("alpacaAmpolMap");
  const { map } = useMap();

  useEffect(() => {
    if (map == null) {
      return;
    }

    const indicated =
      indicatedAmpolServices != null && indicatedAmpolServices.length > 0;

    map.getCanvas().style.cursor = indicated ? "pointer" : "";
  }, [map, indicatedAmpolServices]);

  const getPadding = useCallback(
    (map: mapboxgl.Map) => {
      if (map == null) {
        return 10;
      }

      const padding = DEFAULT_PADDING;
      if (dimensions.width > MIN_WIDTH_FOR_PADDING_LEFT_APPLY) {
        padding.left = LEFT_PADDING;
        padding.top = 70;
        padding.bottom = 30;
      }

      return padding;
    },
    [dimensions]
  );

  useEffect(() => {
    if (map != null) {
      map.setMaxBounds([80, -60, 180, 0]);
    }
  }, [map]);

  useEffect(() => {
    const [first] = indicatedAmpolServices ?? [];

    // If the indicated is offscreen, we need to bring it back
    if (first != null && map != null) {
      if (
        map
          .queryRenderedFeatures(undefined, { layers: ["ampol-services"] })
          .filter((g) => g.properties?.$id === first.$id).length === 0
      ) {
        // const mapBounds = map.getBounds();
        //const newBounds = mapBounds.extend(first.position as [number, number]);
        const newBounds = extendBoundsWhileMaintainingCenter(
          map,
          first.position as [number, number]
        );

        map.fitBounds(newBounds, {
          duration: 1000,
          // padding: getPadding(map),
          // maxZoom: 10,
        });
      }
    }
  }, [indicatedAmpolServices, getPadding, map]);

  useEffect(() => {
    if (map == null || ampolService == null) {
      return;
    }

    // If we have a selection off screen, bring it into view by growing the bounds
    // to include
    const mapBounds = map.getBounds();

    if (
      map
        .queryRenderedFeatures(undefined, { layers: ["ampol-services"] })
        .filter((g) => g.properties?.$id === ampolService.$id).length === 0
    ) {
      const newBounds = mapBounds.extend(
        ampolService.position as [number, number]
      );
      map.fitBounds(newBounds, {
        duration: 1000,
        padding: getPadding(map),
        maxZoom: 10,
      });
    } else {
      map.easeTo({
        center: ampolService.position as [number, number],
        duration: 1000,
        padding: getPadding(map),
      });
    }
  }, [map, ampolService, getPadding]);

  const apply = useCallback(
    (viewport: TargetStore["viewport"]) => {
      if (map == null) {
        return;
      }

      if (Array.isArray(viewport) && viewport.length === 2) {
        map.flyTo({
          center: viewport as [number, number],
          zoom: 13,
          duration: 1000,
          padding: getPadding(map),
        });
      } else {
        map.fitBounds(viewport, {
          duration: 1000,
          padding: getPadding(map),
          maxZoom: 13,
        });
      }
    },
    [map, getPadding]
  );

  const { subscribe } = useSubscribe();

  useEffect(() => {
    return subscribe((state, prevState) => {
      if (state.viewport !== prevState.viewport) {
        apply(state.viewport);
      }
    });
  });

  return null;
};

export default useTargetApply;

function extendBoundsWhileMaintainingCenter(
  map: mapboxgl.Map,
  myPos: mapboxgl.LngLatLike
) {
  const originalCenter = map.getCenter();
  const mapBounds = map.getBounds();

  // Convert myPos to a LngLat object if it's not already
  const newPos = LngLat.convert(myPos);

  // Determine how the bounds need to be extended in longitude and latitude independently
  const boundsLng = [mapBounds.getWest(), mapBounds.getEast()];
  const boundsLat = [mapBounds.getSouth(), mapBounds.getNorth()];

  // Calculate necessary extensions to include the new point, considering each dimension independently
  const extendLng = Math.max(
    0,
    newPos.lng - boundsLng[1],
    boundsLng[0] - newPos.lng
  );
  const extendLat = Math.max(
    0,
    newPos.lat - boundsLat[1],
    boundsLat[0] - newPos.lat
  );

  // Calculate new bounds that include the new point and maintain the center
  // This may require adjusting both dimensions even if the new point is off in only one dimension
  let newWest = boundsLng[0] - extendLng;
  let newEast = boundsLng[1] + extendLng;
  let newSouth = boundsLat[0] - extendLat;
  let newNorth = boundsLat[1] + extendLat;

  // Adjusting based on the original center to ensure it remains the center
  const lngCorrection = originalCenter.lng - (newEast + newWest) / 2;
  const latCorrection = originalCenter.lat - (newNorth + newSouth) / 2;

  newWest += lngCorrection;
  newEast += lngCorrection;
  newSouth += latCorrection;
  newNorth += latCorrection;

  // Create a new bounds object from the adjusted coordinates
  return new LngLatBounds(
    new LngLat(newWest, newSouth),
    new LngLat(newEast, newNorth)
  );
}
